import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import FileUpload from "components/FileUpload";
import DocumentViewerModal from "components/DocumentViewer";
import { CaseCreation, FILE_URL, Remand, RemandStatus } from "types";
import { DateView, errorHandlerOnForm } from "utils/helper";
import {
  addRemand,
  fetchRemands,
  updateRemand,
} from "services/courtCaseService";

type Props = {
  caseData: CaseCreation;
  isProsecutor?: boolean;
  isCoordinator?: boolean;
  isRegistrar?: boolean;
};

type Employee = {
  user?: { id?: string | number; first_name?: string; last_name?: string };
  roles?: string[];
};

const RemandTab: React.FC<Props> = ({
  caseData,
  isProsecutor,
  isCoordinator,
  isRegistrar,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [remands, setRemands] = useState<Remand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [courtSource, setCourtSource] = useState("");
  const [registrarName, setRegistrarName] = useState(
    loginData?.full_name || "",
  );
  const [reason, setReason] = useState("");
  const [remandFile, setRemandFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [prosecutors, setProsecutors] = useState<Employee[]>([]);
  const [selectedProsecutor, setSelectedProsecutor] = useState<
    Record<string, string>
  >({});

  const baseUrl = `${process.env.REACT_APP_API_BASE_URL}${FILE_URL.REMAND}`;
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const load = () => {
    if (!caseData?.id) return;
    setLoading(true);
    fetchRemands({ case_id: String(caseData.id) })
      .then((data) => setRemands(Array.isArray(data) ? data : []))
      .catch(() => setRemands([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.id]);

  // Prosecutor list for the coordinator's assignment.
  useEffect(() => {
    if (!isCoordinator || !loginData?.admin_unit) return;
    fetch(
      `${BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${loginData?.admin_unit}&org_id=${loginData?.org_unit}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((r) => r.json())
      .then((data) =>
        setProsecutors(
          (data || []).filter((d: Employee) =>
            d?.roles?.includes("ProsecutionCaseProsecutor"),
          ),
        ),
      )
      .catch(() => setProsecutors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoordinator, loginData]);

  const handleCreate = () => {
    if (!courtSource.trim()) {
      showTooltip(
        t("court_source_required", { defaultValue: "Source court is required" }),
        "error",
      );
      return;
    }
    if (!remandFile) {
      showTooltip(
        t("remand_file_required", { defaultValue: "Remand file is required" }),
        "error",
      );
      return;
    }
    setSubmitting(true);
    addRemand({
      case: String(caseData?.id),
      court_source: courtSource,
      registrar_name: registrarName,
      remand_reason: reason,
      remand_file: remandFile,
      admin_unit_id: loginData?.admin_unit ? String(loginData.admin_unit) : null,
    })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setShowForm(false);
        setCourtSource("");
        setReason("");
        setRemandFile(null);
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      )
      .finally(() => setSubmitting(false));
  };

  const setStatus = (remand: Remand, status: string, extra?: object) => {
    updateRemand({ id: remand.id, status: status as any, ...extra })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      );
  };

  const assignProsecutor = (remand: Remand) => {
    const id = selectedProsecutor[remand.id || ""];
    const emp = prosecutors.find((p) => String(p.user?.id) === id);
    if (!emp) {
      showTooltip(
        t("no_prosecutors_available_to_assign", {
          defaultValue: "Please select a prosecutor",
        }),
        "error",
      );
      return;
    }
    setStatus(remand, "assigned_to_prosecutor", {
      assigned_prosecutor: `${emp.user?.first_name} ${emp.user?.last_name}`,
      assigned_prosecutor_id: String(emp.user?.id),
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-2 h-8 bg-red-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t("remand", { defaultValue: "Remand" })}
          </h3>
        </div>
        {isRegistrar && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-5 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            + {t("add_remand", { defaultValue: "Add Remand" })}
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1">
                {t("court_source", { defaultValue: "Source Court" })}
                <span className="text-red-500"> *</span>
              </label>
              <input
                type="text"
                value={courtSource}
                onChange={(e) => setCourtSource(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1">
                {t("registrar_name", { defaultValue: "Registrar Name" })}
              </label>
              <input
                type="text"
                value={registrarName}
                onChange={(e) => setRegistrarName(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              />
            </div>
            <div className="flex flex-col md:col-span-2">
              <label className="text-sm text-gray-600 mb-1">
                {t("remand_reason", { defaultValue: "Remand Reason" })}
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              />
            </div>
            <FileUpload
              label={t("remand_file", { defaultValue: "Remand File" })}
              required
              fileTypes="*"
              onChange={setRemandFile}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={handleCreate}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
            >
              {submitting
                ? t("loading", { defaultValue: "Loading..." })
                : t("submit", { defaultValue: "Submit" })}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">
          {t("loading", { defaultValue: "Loading..." })}
        </div>
      ) : remands.length === 0 ? (
        <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-lg">
          {t("no_remands", { defaultValue: "No remands yet" })}
        </div>
      ) : (
        <div className="space-y-4">
          {remands.map((r) => (
            <div
              key={r.id}
              className="border border-gray-200 rounded-xl p-5 bg-white"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h4 className="text-base font-semibold text-gray-900">
                    {r.court_source}
                  </h4>
                  {r.remand_reason && (
                    <p className="text-sm text-gray-600 whitespace-pre-line">
                      {r.remand_reason}
                    </p>
                  )}
                  {r.registrar_name && (
                    <p className="text-xs text-gray-500">
                      {t("registrar_name", { defaultValue: "Registrar Name" })}:{" "}
                      {r.registrar_name}
                    </p>
                  )}
                  {r.assigned_prosecutor && (
                    <p className="text-xs text-gray-500">
                      {t("prosecutor", { defaultValue: "Prosecutor" })}:{" "}
                      {r.assigned_prosecutor}
                    </p>
                  )}
                  {r.created_at && (
                    <p className="text-xs text-gray-400">
                      {DateView(r.created_at)}
                    </p>
                  )}
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 whitespace-nowrap">
                  {t(`remand_status_${r.status}`, {
                    defaultValue:
                      RemandStatus[r.status as keyof typeof RemandStatus] ||
                      r.status,
                  })}
                </span>
              </div>

              {Array.isArray(r.documents) && r.documents.length > 0 && (
                <div className="mt-4">
                  <DocumentViewerModal
                    documents={r.documents as any}
                    baseUrl={baseUrl}
                    layout="list"
                    hideEmptyFile
                  />
                </div>
              )}

              {/* Workflow actions */}
              <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-end gap-3">
                {isRegistrar && r.status === "pending" && (
                  <button
                    type="button"
                    onClick={() => setStatus(r, "forwarded_to_coordinator")}
                    className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                  >
                    {t("forward_to_coordinator", {
                      defaultValue: "Forward to Coordinator",
                    })}
                  </button>
                )}

                {isCoordinator && r.status === "forwarded_to_coordinator" && (
                  <>
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-500 mb-1">
                        {t("prosecutor", { defaultValue: "Prosecutor" })}
                      </label>
                      <select
                        value={selectedProsecutor[r.id || ""] || ""}
                        onChange={(e) =>
                          setSelectedProsecutor((p) => ({
                            ...p,
                            [r.id || ""]: e.target.value,
                          }))
                        }
                        className="border border-gray-300 rounded-lg bg-white p-2 text-sm focus:outline-none focus:ring focus:ring-blue-300"
                      >
                        <option value="">
                          {t("select_prosecutor", {
                            defaultValue: "Select Prosecutor",
                          })}
                        </option>
                        {prosecutors.map((p, i) => (
                          <option key={i} value={p.user?.id || ""}>
                            {p.user?.first_name} {p.user?.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => assignProsecutor(r)}
                      className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                    >
                      {t("assign_prosecutor", {
                        defaultValue: "Assign Prosecutor",
                      })}
                    </button>
                  </>
                )}

                {isProsecutor && r.status === "assigned_to_prosecutor" && (
                  <button
                    type="button"
                    onClick={() => setStatus(r, "on_hearing")}
                    className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                  >
                    {t("start_hearing", { defaultValue: "Start Hearing" })}
                  </button>
                )}

                {isProsecutor && r.status === "on_hearing" && (
                  <button
                    type="button"
                    onClick={() => setStatus(r, "completed")}
                    className="px-5 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
                  >
                    {t("mark_completed", { defaultValue: "Mark Completed" })}
                  </button>
                )}

                {isProsecutor &&
                  (r.status === "assigned_to_prosecutor" ||
                    r.status === "on_hearing") && (
                    <p className="text-xs text-gray-500">
                      {t("remand_hearing_via_daily_report", {
                        defaultValue:
                          "Record hearings from the Report tab (Daily Report).",
                      })}
                    </p>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RemandTab;
